import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, MapPin } from "lucide-react";
import { CANDIDATE, SLATE } from "@/data/content";
import { Marked, Asterisk, ArrowDraw, HandTag } from "@/components/campaign/HandDrawn";

const Avatar = ({ candidate }) => {
  if (candidate.photo) {
    return (
      <img
        src={candidate.photo}
        alt={candidate.name}
        className="relative w-full aspect-square object-cover object-center rounded-2xl border-2 border-ink/85"
      />
    );
  }
  return (
    <div className="relative w-full aspect-square border-2 border-ink/85 bg-forest text-cream flex items-center justify-center">
      <span className="font-display font-black text-8xl tracking-tight">
        {candidate.initials}
      </span>
    </div>
  );
};

const Slate = () => {
  return (
    <main data-testid="slate-page">
      {/* HERO */}
      <section className="section border-b border-ink/25">
        <div className="container-x grid gap-12 md:grid-cols-12 items-end">
          <div className="md:col-span-8">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> Hillsborough 40 · The slate
            </p>
            <h1 className="mt-4 font-editorial font-semibold text-5xl md:text-7xl tracking-tight leading-[0.92] text-ink">
              Vote the whole <br />
              <Marked>slate</Marked>.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink/75 leading-relaxed text-lg">
              Hillsborough 40 is a <span className="italic font-semibold text-terracotta text-base">floterial</span>{" "}
              district — you cast your vote for multiple representatives. Meet the neighbors
              running with Rob.
            </p>
          </div>
        </div>
      </section>

      {/* FLOTERIAL EXPLAINER */}
      <section className="section bg-parchment" data-testid="floterial-explainer">
        <div className="container-x grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> What&apos;s a floterial?
            </p>
            <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-5xl leading-[0.95] tracking-tight text-ink">
              One district. <br />
              <Marked>Many voices</Marked>.
            </h2>
          </div>
          <div className="md:col-span-7 md:pl-10 md:border-l-2 md:border-ink space-y-5 text-lg leading-relaxed text-ink/80">
            <p>
              In New Hampshire, a <span className="font-bold">floterial district</span> is a
              legislative district that sits on top of several smaller ward-based districts and
              elects <span className="font-bold">additional at-large representatives</span> who
              serve every neighborhood inside it.
            </p>
            <p>
              <span className="font-bold">Hillsborough District 40</span> covers Manchester{" "}
              <span className="font-bold">Wards 1, 3, 10, 11 &amp; 12</span> — a stretch of
              neighborhoods across the city&apos;s west, north, and east sides. When you go to
              vote, you&apos;ll see multiple names for State Representative on the ballot. You get
              to fill in a bubble for each of them.
            </p>
            <p>
              That means <span className="italic font-semibold text-terracotta text-lg">your ballot builds a team</span>. If you want a
              Concord delegation that shows up for renters, workers, students, and neighbors —
              vote all four of us.
            </p>
          </div>
        </div>
      </section>

      {/* WARDS BAR */}
      <section className="bg-canvas border-y border-ink/25 py-10">
        <div className="container-x flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="overline inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" /> The district
          </p>
          <p className="font-display font-black uppercase text-2xl md:text-3xl text-ink tracking-tight text-center">
            Manchester · Wards 1, 3, 10, 11 &amp; 12
          </p>
          <div className="flex gap-2">
            {["1", "3", "10", "11", "12"].map((w) => (
              <div
                key={w}
                className="border border-rule bg-white/5 py-2 px-3 font-display font-black text-xl text-ink shadow-[3px_3px_0_0_#F58A3F]"
              >
                {w}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CANDIDATE CARDS */}
      <section className="section" data-testid="slate-cards">
        <div className="container-x">
          <p className="overline inline-flex items-center gap-2 mb-6">
            <Asterisk className="h-4 w-4" /> Meet the team
          </p>
          <h2 className="font-editorial font-semibold text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink max-w-4xl">
            Four <Marked>Democrats</Marked>. <br />
            One <Marked color="#F58A3F">district</Marked>.
          </h2>

          <div className="mt-16 grid gap-10 md:grid-cols-2">
            {SLATE.map((c) => (
              <article
                key={c.id}
                className="relative bg-white/5 border border-rule transition-all duration-200 "
                data-testid={`slate-candidate-${c.id}`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-5">
                  <div className="sm:col-span-2 relative">
                    <Avatar candidate={c} />
                    {c.isMe && (
                      <span className="absolute top-3 left-3 bg-terracotta text-cream italic font-medium text-base px-3 py-1 -rotate-3">
                        that&apos;s me
                      </span>
                    )}
                    {c.newGuy && (
                      <span className="absolute top-3 left-3 bg-terracotta text-cream italic font-semibold text-sm tracking-wide px-3 py-1.5 rounded-full shadow-lg -rotate-3">
                        I&apos;m the new guy
                      </span>
                    )}
                  </div>
                  <div className="sm:col-span-3 p-6 md:p-7 border-t-2 sm:border-t-0 sm:border-l-2 border-ink">
                    <HandTag>{c.ward}</HandTag>
                    <h3 className="mt-4 font-display font-black uppercase text-2xl md:text-3xl text-ink leading-tight tracking-tight">
                      {c.name}
                    </h3>
                    <p className="mt-2 text-sm font-bold uppercase tracking-wide text-terracotta">
                      {c.role}
                    </p>
                    <p className="mt-4 text-ink/80 leading-relaxed text-[0.95rem]">
                      {c.bio}
                    </p>
                    <ul className="mt-5 flex flex-wrap gap-3">
                      {c.links.map((l) =>
                        l.internal ? (
                          <li key={l.href}>
                            <Link
                              to={l.href}
                              className="inline-flex items-center gap-1 text-ink font-bold uppercase tracking-wide text-xs hover:text-terracotta border-b-2 border-ink pb-0.5"
                            >
                              {l.label} <ArrowUpRight size={12} />
                            </Link>
                          </li>
                        ) : (
                          <li key={l.href}>
                            <a
                              href={l.href}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-ink font-bold uppercase tracking-wide text-xs hover:text-terracotta border-b-2 border-ink pb-0.5"
                            >
                              {l.label} <ExternalLink size={12} />
                            </a>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-cream section relative overflow-hidden">
        <div className="container-x grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-8">
            <p className="overline !text-terracotta inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> Election day
            </p>
            <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-6xl leading-[0.9]">
              Fill in <Marked color="#F58A3F">all four</Marked><br />
              bubbles.
            </h2>
            <p className="mt-8 text-cream/80 max-w-xl text-lg">
              A floterial district gives you the chance to send a whole team to Concord —{" "}
              <span className="italic font-semibold text-terracotta text-base">use every vote</span>.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-4 md:justify-end">
            <Link to="/volunteer" className="btn-ghost !border-cream !text-cream hover:!bg-cream hover:!text-graphite" data-testid="slate-volunteer-btn">
              Volunteer
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Slate;
